import { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  calculateWaterLoss,
  calculateDrinkingWater,
  calculateDehydration,
  mapUiFeedToEhe,
  statusKey,
} from "../utils/eheCalculator";
import type { Intensity } from "../utils/eheCalculator";
import type { DailyLog, EheResult, Horse, LogFormValues } from "./types";

export function todayId(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export interface UseHorseResult {
  horse: Horse | null;
  loading: boolean;
  error: Error | null;
}

export function useHorse(
  yardId: string | null | undefined,
  horseId: string | null | undefined
): UseHorseResult {
  const [horse, setHorse] = useState<Horse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!yardId || !horseId) {
      setLoading(false);
      return;
    }
    const ref = doc(db, "yards", yardId, "horses", horseId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setHorse({ id: snap.id, ...(snap.data() as Omit<Horse, "id">) });
        } else {
          setHorse(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [yardId, horseId]);

  return { horse, loading, error };
}

export interface UseHorseLogsResult {
  logs: DailyLog[];
  loading: boolean;
  error: Error | null;
}

export function useHorseLogs(
  yardId: string | null | undefined,
  horseId: string | null | undefined,
  days: number = 14
): UseHorseLogsResult {
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!yardId || !horseId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "yards", yardId, "horses", horseId, "dailyLogs"),
      orderBy("__name__", "desc"),
      limit(days)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setLogs(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DailyLog, "id">) }))
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [yardId, horseId, days]);

  return { logs, loading, error };
}

export interface UseRecentLogsForHorsesResult {
  logsMap: Record<string, DailyLog[]>;
  loading: boolean;
}

export function useRecentLogsForHorses(
  yardId: string | null | undefined,
  horseIds: string[] | null | undefined,
  days: number = 7
): UseRecentLogsForHorsesResult {
  const [logsMap, setLogsMap] = useState<Record<string, DailyLog[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const idsKey = (horseIds || []).join(",");

  useEffect(() => {
    if (!yardId || !horseIds || horseIds.length === 0) {
      setLogsMap({});
      setLoading(false);
      return;
    }
    setLoading(true);
    const remaining = new Set<string>(horseIds);
    const unsubs = horseIds.map((hid) => {
      const q = query(
        collection(db, "yards", yardId, "horses", hid, "dailyLogs"),
        orderBy("__name__", "desc"),
        limit(days)
      );
      return onSnapshot(q, (snap) => {
        setLogsMap((prev) => ({
          ...prev,
          [hid]: snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DailyLog, "id">) })),
        }));
        if (remaining.has(hid)) {
          remaining.delete(hid);
          if (remaining.size === 0) setLoading(false);
        }
      });
    });
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yardId, idsKey, days]);

  return { logsMap, loading };
}

export interface UseDailyLogResult {
  log: DailyLog | null;
  loading: boolean;
  error: Error | null;
}

export function useDailyLog(
  yardId: string | null | undefined,
  horseId: string | null | undefined,
  dateId: string | null | undefined
): UseDailyLogResult {
  const [log, setLog] = useState<DailyLog | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!yardId || !horseId || !dateId) {
      setLoading(false);
      return;
    }
    const ref = doc(db, "yards", yardId, "horses", horseId, "dailyLogs", dateId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setLog(
          snap.exists()
            ? { id: snap.id, ...(snap.data() as Omit<DailyLog, "id">) }
            : null
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [yardId, horseId, dateId]);

  return { log, loading, error };
}

export interface SaveDailyLogArgs {
  yardId: string;
  horseId: string;
  dateId: string;
  userId: string;
  horse: Horse;
  values: LogFormValues;
}

export async function saveDailyLog({
  yardId,
  horseId,
  dateId,
  userId,
  horse,
  values,
}: SaveDailyLogArgs): Promise<EheResult | null> {
  const weight = horse.weightKg;
  const tempC = values.ambientTempC ?? 18;
  const exHrs = (values.exercise?.mins || 0) / 60;
  const intensity: Intensity = (values.exercise?.intensity || "light") as Intensity;
  const feedList = (values.feed || [])
    .filter((f) => f.kg > 0)
    .map((f) => ({ type: mapUiFeedToEhe(f.type), amount: f.kg }));

  let ehe: EheResult | null = null;
  if (feedList.length && weight && !isNaN(values.waterL)) {
    const wlTotal = calculateWaterLoss({
      weight,
      exerciseDurationHrs: exHrs,
      exerciseIntensity: intensity,
      temperature: tempC,
      feedList,
    });
    const { wlDrink, wlFeed, wlMeta } = calculateDrinkingWater({
      wlTotal,
      weight,
      feedList,
    });
    const dehydration = calculateDehydration({
      wlTotal,
      wlFeed,
      wlMeta,
      actualDrunk: values.waterL,
      weight,
    });
    ehe = {
      lossL: Number(wlTotal.toFixed(2)),
      targetL: Number(wlDrink.toFixed(2)),
      actualL: Number(values.waterL),
      deficitL: Number(Math.max(0, wlDrink - values.waterL).toFixed(2)),
      deficitPct: Number(dehydration.percentage),
      status: dehydration.status,
      statusKey: statusKey(dehydration.status),
    };
  }

  const ref = doc(db, "yards", yardId, "horses", horseId, "dailyLogs", dateId);
  const existing = await getDoc(ref);

  const payload: Record<string, unknown> = {
    water: values.waterL ?? null,
    ambientTempC: values.ambientTempC ?? null,
    exercise: values.exercise || { mins: 0, intensity: null },
    feed: values.feed || [],
    behaviour: values.behaviour || null,
    droppings: values.droppings || { tag: null, count: null },
    notes: values.notes || null,
    ehe,
    loggedBy: userId,
    updatedAt: serverTimestamp(),
  };
  if (!existing.exists()) payload["createdAt"] = serverTimestamp();

  await setDoc(ref, payload, { merge: true });
  return ehe;
}
